/**
 * User API
 *
 * API functions for fetching user profiles
 */

import { buildApiUrl, handleApiResponse, ApiError } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';
import type { ContractorProfile } from '@hummii/shared-types';
import type { ClientProfile, ProfileReview } from '../types';

export interface FetchProfileParams {
  userId: string;
}

/**
 * Fetch contractor profile by user ID
 */
export async function fetchContractorProfile(
  params: FetchProfileParams,
): Promise<ContractorProfile | null> {
  const { userId } = params;

  try {
    const url = buildApiUrl(`/contractors/${userId}`);
    const response = await fetchWithTokenRefresh(url, {
      credentials: 'include', // Include cookies for auth
    });
    return await handleApiResponse<ContractorProfile>(response);
  } catch (error) {
    // Don't log 404 as error - it's expected for non-contractor users
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    console.error('Failed to fetch contractor profile:', error);
    return null;
  }
}

/**
 * Fetch own contractor profile (uses /contractors/me endpoint)
 */
export async function fetchMyContractorProfile(): Promise<ContractorProfile | null> {
  try {
    const url = buildApiUrl('/contractors/me');
    const response = await fetchWithTokenRefresh(url, {
      credentials: 'include',
    });
    return await handleApiResponse<ContractorProfile>(response);
  } catch (error) {
    console.error('Failed to fetch own contractor profile:', error);
    return null;
  }
}

/**
 * Fetch client profile by user ID
 */
export async function fetchClientProfile(
  params: FetchProfileParams,
): Promise<ClientProfile | null> {
  const { userId } = params;

  try {
    const url = buildApiUrl(`/users/${userId}/client-profile`);
    const response = await fetch(url);
    return await handleApiResponse<ClientProfile>(response);
  } catch (error) {
    // Don't log 404 as error - it's expected for non-client users
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    console.error('Failed to fetch client profile:', error);
    return null;
  }
}

/**
 * Map backend review response to frontend ProfileReview type
 */
function mapReviewResponse(review: any): ProfileReview {
  return {
    id: review.id,
    rating: review.rating || review.averageRating, // Backend may use different field
    comment: review.comment || null,
    criteriaRatings: review.criteriaRatings || {},
    isEdited: review.isEdited || false,
    createdAt: new Date(review.createdAt),
    reviewer: {
      id: review.reviewerActor?.id || review.reviewerActorId || '',
      name: review.reviewerActor?.displayName || review.reviewerActor?.name || 'Anonymous',
      avatarUrl: review.reviewerActor?.avatarUrl || null,
      isVerified: review.reviewerActor?.user?.isEmailVerified || false,
    },
    response: review.response
      ? {
          id: review.response.id,
          content: review.response.content,
          createdAt: new Date(review.response.createdAt),
        }
      : null,
  };
}

/**
 * Fetch profile reviews
 */
export async function fetchProfileReviews(userId: string): Promise<ProfileReview[]> {
  try {
    const url = buildApiUrl(`/reviews/user/${userId}`);
    const response = await fetchWithTokenRefresh(url, {
      credentials: 'include',
    });
    const result = await handleApiResponse<{ data: any[]; meta: any }>(response);
    // Backend returns paginated response { data: [], meta: {} }
    // Map backend response to frontend ProfileReview type
    const reviews = (result?.data || []).map(mapReviewResponse);
    return reviews;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      // No reviews found → empty list without noisy error
      return [];
    }
    console.error('Failed to fetch profile reviews:', error);
    return [];
  }
}
